import {Button, Col, Flex, Form, Input, Row, Select} from 'antd';
import FormItem from 'antd/es/form/FormItem';
import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {filterSelectOptions} from '../../../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../../../components/Loader/Loader';
import MainTableTitle from '../../../../../components/Table/Utils/MainTableTitle';
import {CommonErrors} from '../../../../../constants/Common';
import {allRoutes} from '../../../../../constants/Routes';
import {APITypeEnum} from '../../../../../services/interface/tableAPIType';
import designation from '../../../../../services/modules/JSON/designation';
import addRole from '../../../../../services/modules/UserManager/addRole';
import userPermission from '../../../../../services/modules/UserManager/addUser';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../../../../store/StaticJson/jsonStore';
import useUserStore from '../../../../../store/userStore/userStore';
import {FirstCap} from '../../../../../utils/FormUtils/FirstCap';
import RegEx from '../../../../../utils/FormUtils/RegEx';
import showMessage from '../../../../../utils/showMessage';
import {addUserKey, addUserLabel} from '../Constants/addUserKeyLabel';

const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [editId, setEditId] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // const up = useUserStore.getState().userPermission;

  const user = useUserStore.getState().userDetails;
  const companyID = useCompanyStore.getState().companyId;
  const designationOption = useJSONStore.getState().designation;

  useEffect(() => {
    if (!!location.state) {
      setEditId(location.state._id);
      form.setFieldsValue({
        ...location.state,
        roles: location?.state.roles?.[0]
      });
    } else {
      form.resetFields();
    }
  }, [location.state]);

  useEffect(() => {
    Promise.all([designation(), addRole(APITypeEnum.GET_ALL)])
      .then(([_, res2]) => {
        setRoles(res2);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleSubmit = (userDetails: any) => {
    setIsSubmitted(true);
    userPermission(
      editId ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
      {
        ...userDetails,
        roles: [userDetails.roles],
        isActive: true,
        company: companyID,
        updatedBy: user._id,
        createdBy: editId ? userDetails.createdBy : user._id
      },
      editId
    )
      .then((res) => {
        showMessage(res);
        setIsSubmitted(false);
        if (res?.status !== 'error') {
          navigate(allRoutes.setting.userManager);
        }
      })
      .catch(() => {})
      .finally(() => {});
  };

  return (
    <Flex className="userFlex">
      {isLoading ? (
        <Loader isHome={true} />
      ) : (
        <>
          <MainTableTitle title={`${editId ? 'Update' : 'Add'} User`} />
          <Flex className="content">
            <Form
              form={form}
              onFinish={handleSubmit}
              style={{width: '100%'}}
              onValuesChange={() => {
                setFormChanged(true);
              }}
              // disabled={!up.setting.userManager.shouldEdit}
            >
              <Row gutter={{xs: 6, sm: 12, md: 18, lg: 24}}>
                <Col span={6}>
                  <Form.Item
                    name={addUserKey.FIRST_NAME}
                    label={addUserLabel.FIRST_NAME}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(addUserLabel.FIRST_NAME)
                      },
                      {
                        max: 100,
                        message: `${addUserLabel.FIRST_NAME} Should not be more than 100 Characters`
                      },
                      {
                        min: 2,
                        message: `${addUserLabel.FIRST_NAME} Should be more than 1 Characters`
                      },
                      {
                        pattern: RegEx.names,
                        message:
                          'No special characters , Empty spaces or Numbers are Allowed'
                      },
                      {
                        whitespace: true,
                        message: CommonErrors.mandatory(addUserLabel.FIRST_NAME)
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Input
                      className="addUserInputs"
                      placeholder={addUserLabel.FIRST_NAME}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.value = FirstCap(target.value);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={addUserKey.LAST_NAME}
                    label={addUserLabel.LAST_NAME}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(addUserLabel.LAST_NAME)
                      },
                      {
                        max: 100,
                        message: `${addUserLabel.LAST_NAME} Should not be more than 100 Characters`
                      },
                      {
                        min: 2,
                        message: `${addUserLabel.LAST_NAME} Should be more than 1 Characters`
                      },
                      {
                        pattern: RegEx.names,
                        message:
                          'No special characters , Empty spaces or Numbers are Allowed'
                      },
                      {
                        whitespace: true,
                        message: CommonErrors.mandatory(addUserLabel.LAST_NAME)
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Input
                      className="addUserInputs"
                      placeholder={addUserLabel.LAST_NAME}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.value = FirstCap(target.value);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={addUserKey.MOBILE}
                    label={addUserLabel.MOBILE}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(addUserLabel.MOBILE)
                      },
                      {
                        pattern: RegEx.mobile,
                        message: CommonErrors.validField(addUserLabel.MOBILE)
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Input
                      type="text"
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        const value = target.value;
                        target.value = value.replace(RegEx.replaceNumber, '');
                      }}
                      placeholder={addUserLabel.MOBILE}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={addUserKey.EMAIL}
                    label={addUserLabel.EMAIL}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(addUserLabel.EMAIL)
                      },
                      {
                        min: 5,
                        message: `${addUserLabel.EMAIL} Should be more than 4 Characters`
                      },
                      {
                        max: 320,
                        message: `${addUserLabel.EMAIL} Should not be more than 320 Characters`
                      },
                      {
                        pattern: RegEx.email,
                        message: CommonErrors.validField(addUserLabel.EMAIL)
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Input
                      className="addUserInputs"
                      placeholder={addUserLabel.EMAIL}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="roles"
                    label="Role"
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory('Role')
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Role"
                      options={roles?.map((item) => ({
                        value: item?._id,
                        label: item?.title
                      }))}
                      filterOption={filterSelectOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={addUserKey.DESIGNATION}
                    label={addUserLabel.DESIGNATION}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory('Designation')
                      }
                    ]}
                    labelCol={{span: 24}}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder={addUserLabel.DESIGNATION}
                      options={designationOption}
                      filterOption={filterSelectOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <FormItem>
                <Button
                  className="buttonSave"
                  type="primary"
                  htmlType="submit"
                  disabled={!formChanged || isSubmitted}
                  loading={isSubmitted}
                >
                  Save
                </Button>
              </FormItem>
            </Form>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default AddUser;
